import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getLogoUrl } from '@/services/branding';

const LOGO_CACHE_KEY = 'forja:logo:v1';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Fallback logo URL - preloaded in index.html for instant display
const FALLBACK_LOGO_URL = '/favicon.png';

interface BrandingContextType {
  logoUrl: string | null;
  isLoading: boolean;
  refreshLogo: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

// Read cache synchronously on initialization, with fallback
const getCachedLogo = (): string => {
  try {
    const cached = localStorage.getItem(LOGO_CACHE_KEY);
    if (cached) {
      const { url, timestamp } = JSON.parse(cached);
      // Invalidate cache if it contains the old logo URL
      if (url && url.includes('logo-1768440033445')) {
        localStorage.removeItem(LOGO_CACHE_KEY);
        return FALLBACK_LOGO_URL;
      }
      if (Date.now() - timestamp < CACHE_DURATION && url) {
        return url;
      }
    }
  } catch {
    // Fall through to fallback
  }
  return FALLBACK_LOGO_URL;
};

export function BrandingProvider({ children }: { children: ReactNode }) {
  // Initialize with cached or fallback logo - never null, never loading
  const [logoUrl, setLogoUrl] = useState<string | null>(() => getCachedLogo());
  const [isLoading, setIsLoading] = useState(false);

  const refreshLogo = useCallback(async () => {
    try {
      const url = await getLogoUrl();
      setLogoUrl(url);
      localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify({
        url,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error fetching logo:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(LOGO_CACHE_KEY);
      if (cached) {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) return;
      }
    } catch { /* fall through to refresh */ }
    refreshLogo();
  }, [refreshLogo]);

  const value = useMemo(() => ({ logoUrl, isLoading, refreshLogo }), [logoUrl, isLoading, refreshLogo]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
